import { useEffect, useRef, useState } from "react";
import { fetchTurbineList } from "../../api/channelSelection";
import type { TurbineInfo } from "../../types/dialog-types";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";
import { getTigerResource } from "../../utils/tigerResource";
import LangDateInput from "../LangDateInput";
import { PickerKey, pickerInitialDir, rememberPickerDir } from "../../utils/pickerFolder";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Matches ALL_TURBINES_SENTINEL in src-tauri/src/commands/backup_data.rs. */
const ALL_TURBINES = "__ALL_TURBINES__";

interface BackupDataDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Port of the C++ `backup_data_shell_c`. Collects a turbine (or "All Turbines"),
 * a start/end date and a backup folder, then invokes the `backup_data` command,
 * which runs the day-by-day copy in-process on a worker thread (no separate
 * `tiger_backup_data` exe). Live-mode only (see MenuBar).
 */
export function BackupDataDialog({ open, onClose }: BackupDataDialogProps) {
  const [turbines, setTurbines] = useState<TurbineInfo[]>([]);
  const [turbine, setTurbine] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [location, setLocation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Dates default to today only on the first open; user edits persist after.
  const defaultsAppliedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (!defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setStartDate(today);
      setEndDate(today);
    }
    fetchTurbineList().then((list) => {
      setTurbines(list);
      setTurbine((cur) => cur || list[0]?.config_name || "");
    });
  }, [open]);

  const handleBrowse = async () => {
    if (!isTauri()) return;
    const { open: openDialog } = await import("@tauri-apps/plugin-dialog");
    const picked = await openDialog({
      directory: true,
      defaultPath: await pickerInitialDir(PickerKey.backup, turbine),
      title: getTigerResource("TIGER_BROWSE_SELECT"),
    });
    if (typeof picked === "string") {
      rememberPickerDir(PickerKey.backup, picked, true);
      setLocation(picked);
    }
  };

  const handleOk = async () => {
    setError("");
    if (!location.trim()) {
      setError(getTigerResource("TIGER_BACKUP_LOCATION_ERROR"));
      return;
    }
    if (!isTauri()) {
      setError(getTigerResource("TIGER_BACKUP_DESKTOP_ONLY"));
      return;
    }
    setBusy(true);
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("backup_data", {
        configName: turbine,
        startDate,
        endDate,
        location,
      });
      onClose();
    } catch (e) {
      setError(`${e}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const title = getTigerResource("TIGER_BACKUP_DATA_TITLE");

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose} disabled={busy}>
            &#x2715;
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.card}>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE")}</label>
              <select style={styles.select} value={turbine} onChange={(e) => setTurbine(e.target.value)}>
                {turbines.map((t) => (
                  <option key={t.config_name} value={t.config_name}>
                    {t.turbine_name}
                  </option>
                ))}
                {turbines.length > 1 && (
                  <option value={ALL_TURBINES}>{getTigerResource("TIGER_ALL_TURBINES")}</option>
                )}
              </select>
            </div>
            <div style={styles.fieldGroup}>
              <div style={styles.field}>
                <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_START_DATE")}</label>
                <LangDateInput style={styles.input} value={startDate} onChange={setStartDate} />
              </div>
              <div style={styles.field}>
                <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_END_DATE")}</label>
                <LangDateInput style={styles.input} value={endDate} onChange={setEndDate} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>{getTigerResource("TIGER_BACKUP_DATA_LOCATION")}</label>
              <div style={styles.fieldRow}>
                <input style={styles.input} type="text" readOnly value={location} aria-label={getTigerResource("TIGER_BACKUP_LOCATION_FIELD")} />
                <button className="btn-acrylic-secondary" onClick={handleBrowse}>
                  {getTigerResource("TIGER_BROWSE_BUTTON")}
                </button>
              </div>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onClose} disabled={busy}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
          <button className="btn-acrylic-primary" onClick={handleOk} disabled={busy}>
            {busy ? getTigerResource("TIGER_RES_WORKING") : getTigerResource("TIGER_RES_OK")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  // 360 tall: the card needs 237px and a 340px dialog only leaves 232px for the
  // body, so the whole form used to sit behind a 5px scrollbar.
  dialog: acrylicDialogSurface({ width: 480, height: 360, minWidth: 400, minHeight: 320 }),
  error: { color: "#b91c1c", fontSize: 12 },
} as const;
